import { AnswerFollowUpButton } from "../site-workflows/CorrectiveActions"
import { controlAnswerKey } from "../../utils/regulatoryIdentity"
import { memo, useCallback, useMemo, useRef, useEffect, useState } from "react"
import { Alert, Button, Input, Image, Radio, Select, Space, Tag } from "antd"
import {
  RegulatoryControlAnswer,
  RegulatoryControlAnswerStatus,
  UUID,
} from "../../generated/proto-ts/main"
import { ICloudRegulatoryControlPoint } from "../../types"
import { regulatoryUuid } from "../../utils/regulatoryPresentation"
import { uuidToPbUUID } from "../../utils/utils"
import {
  answerFields,
  answerStatuses as statuses,
  severityLabels,
} from "../../utils/regulatoryAnswers"
import { ControlHeading, ControlDefinition, RecordedFinding } from "./RegulatoryControlCard"
import { UploadImageListV2 } from "../commons-ts/uploadImageV2"
import type { AnswerSaveState } from "./useRegulatoryAnswers"
const EvidenceImages = memo(UploadImageListV2)
const EMPTY_PICTURES: UUID[] = []
export const QuestionnaireControlCard = memo(function QuestionnaireControlCard({
  cp,
  answer,
  state,
  readOnly,
  loading,
  error,
  onUpdate,
  onFlush,
  retry,
  expansion,
}: {
  expansion?: { sequence: number; expanded: boolean }
  cp: ICloudRegulatoryControlPoint
  answer?: RegulatoryControlAnswer
  state?: AnswerSaveState
  readOnly?: boolean
  loading?: boolean
  error?: string
  onUpdate: (
    cp: ICloudRegulatoryControlPoint,
    patch: Partial<RegulatoryControlAnswer>,
    debounce?: boolean,
  ) => void
  onFlush?: (point: string) => void
  retry?: (point: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    if (expansion) {
      if (!expansion.expanded) onFlush?.(controlAnswerKey(cp))
      setExpanded(expansion.expanded)
    }
  }, [expansion])
  const toggle = () => {
    if (expanded) onFlush?.(controlAnswerKey(cp))
    setExpanded((value) => !value)
  }
  const p = cp.Definition
  const status = answer?.status ?? RegulatoryControlAnswerStatus.UNKNOWN_STATUS
  const pictureKey = (answer?.pictures ?? EMPTY_PICTURES).map(regulatoryUuid).join(",")
  const pictures = useMemo(() => answer?.pictures ?? EMPTY_PICTURES, [pictureKey])
  const latest = useRef(answer)
  latest.current = answer
  const update = useCallback(
    (patch: Partial<RegulatoryControlAnswer>, debounce = false) => onUpdate(cp, patch, debounce),
    [cp, onUpdate],
  )
  const setPictures = useCallback<React.Dispatch<React.SetStateAction<UUID[] | null>>>(
    (next) => {
      const old = latest.current?.pictures ?? EMPTY_PICTURES
      update({ pictures: typeof next === "function" ? next(old) ?? [] : next ?? [] })
    },
    [update],
  )
  return (
    <article className="reg-control-card" onBlurCapture={() => onFlush?.(controlAnswerKey(cp))}>
      <ControlHeading point={cp} answer={answer} />
      {!readOnly && answer && regulatoryUuid(answer.uuid) && (
        <AnswerFollowUpButton
          answerUuid={regulatoryUuid(answer.uuid)!}
          disabled={loading || !!error || state?.state === "pending" || state?.state === "failed"}
        />
      )}
      {!readOnly &&
        answer &&
        cp.PointRevisionUUID &&
        !regulatoryUuid(answer.control_point_revision_uuid) && (
          <Button
            disabled={loading || !!error}
            onClick={() =>
              update({
                control_point_revision_uuid: uuidToPbUUID(cp.PointRevisionUUID!),
              })
            }
          >
            Confirmer la réponse sur cette révision
          </Button>
        )}
      {state && (
        <Tag color={state.state === "failed" ? "red" : state.state === "saved" ? "green" : "blue"}>
          {state.state === "saved"
            ? "Enregistré"
            : state.state === "failed"
              ? "Échec d’enregistrement"
              : "Enregistrement…"}
        </Tag>
      )}
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Radio.Group
          aria-label={`Résultat : ${cp.Text}`}
          options={statuses}
          value={status}
          disabled={readOnly || loading || !!error}
          onChange={(e) => update({ status: e.target.value })}
        />
        <Button
          aria-expanded={expanded}
          aria-controls={`answer-editor-${controlAnswerKey(cp).replace(/\//g, "-")}`}
          onClick={toggle}
        >
          {expanded ? "Réduire le constat" : readOnly ? "Voir le constat" : "Modifier le constat"}
        </Button>
        {!expanded && (
          <div className="reg-compact-summary">
            <p>
              {answer?.comment
                ? `${answer.comment.slice(0, 180)}${answer.comment.length > 180 ? "…" : ""}`
                : "Aucun constat renseigné"}
            </p>
            <Space wrap>
              {answer?.measurement && <Tag>Mesure : {answer.measurement.slice(0, 80)}</Tag>}
              {answer?.severity && (
                <Tag>Gravité : {severityLabels[answer.severity] ?? answer.severity}</Tag>
              )}
              {answer?.due_date && <Tag>Échéance : {answer.due_date}</Tag>}
              {!!pictures.length && <Tag>{pictures.length} photo(s)</Tag>}
              {answer?.recommendation && <Tag>Préconisation renseignée</Tag>}
              {answer?.immediate_action && <Tag>Mesure immédiate renseignée</Tag>}
              {answer?.photo_exception && <Tag>Absence de photo justifiée</Tag>}
            </Space>
          </div>
        )}
        {expanded && (
          <div
            id={`answer-editor-${controlAnswerKey(cp).replace(/\//g, "-")}`}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <ControlDefinition point={cp} />
              {p?.photoRequired &&
                !answer?.pictures?.length &&
                !answer?.photo_exception?.trim() && (
                  <Alert type="warning" message="Photographie requise : aucune photo jointe." />
                )}
              {p?.evidenceRequired && !answer?.pictures?.length && !answer?.comment?.trim() && (
                <Alert
                  type="warning"
                  message="Justificatif requis : ajoutez les éléments disponibles."
                />
              )}
              {!readOnly && (
                <Input.TextArea
                  aria-label={`Commentaire : ${cp.Text}`}
                  placeholder="Constat / observations"
                  rows={3}
                  value={answer?.comment ?? ""}
                  disabled={readOnly || loading || !!error}
                  onChange={(e) => update({ comment: e.target.value }, true)}
                />
              )}
              {readOnly && !!answer?.pictures.length && (
                <Image.PreviewGroup>
                  {answer.pictures.map((pic) => (
                    <Image
                      key={regulatoryUuid(pic)}
                      width={100}
                      src={`/api/uploads/${regulatoryUuid(pic)}`}
                      alt={`Preuve : ${cp.Text}`}
                    />
                  ))}
                </Image.PreviewGroup>
              )}
              {readOnly ? (
                <RecordedFinding answer={answer} />
              ) : (
                <div className="reg-answer-grid">
                  {answerFields.map((field) => (
                    <label key={field.key}>
                      {field.label}
                      {field.key === "measurement" && p?.measurementRequired ? " (attendue)" : ""}
                      <Input.TextArea
                        aria-label={`${field.label} : ${cp.Text}`}
                        autoSize={{ minRows: 2, maxRows: 8 }}
                        value={answer?.[field.key] ?? ""}
                        disabled={loading || !!error}
                        onChange={(e) => update({ [field.key]: e.target.value }, true)}
                      />
                      <small>
                        {Array.from(answer?.[field.key] ?? "").length}/{field.max}
                      </small>
                    </label>
                  ))}
                  <label>
                    Gravité interne
                    <Select
                      aria-label={`Gravité interne : ${cp.Text}`}
                      value={answer?.severity || ""}
                      disabled={loading || !!error}
                      options={[
                        { value: "", label: "Non classée" },
                        ...Object.entries(severityLabels).map(([value, label]) => ({
                          value,
                          label,
                        })),
                      ]}
                      onChange={(severity) => update({ severity })}
                    />
                  </label>
                  <label>
                    Échéance proposée
                    <Input
                      aria-label={`Échéance proposée : ${cp.Text}`}
                      type="date"
                      value={answer?.due_date ?? ""}
                      disabled={loading || !!error}
                      onChange={(e) => update({ due_date: e.target.value })}
                    />
                  </label>
                </div>
              )}
              {!readOnly && !loading && !error && (
                <EvidenceImages pictures={pictures} setPictures={setPictures} />
              )}
            </Space>
          </div>
        )}
        {state?.state === "failed" && (
          <Alert
            showIcon
            type="error"
            message={state.error}
            action={<Button onClick={() => retry?.(controlAnswerKey(cp))}>Réessayer</Button>}
          />
        )}
      </Space>
    </article>
  )
})
